import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./pages.css";
import rawContent from "./TermsAndConditions.md?raw";
import { parseMarkdownDoc } from "./parseMarkdownDoc";
import { SUPPORT_EMAIL } from "../../utils/supportContacts";

const { lastUpdated: LAST_UPDATED, sections: SECTIONS } = parseMarkdownDoc(
  rawContent.replaceAll("{{SUPPORT_EMAIL}}", SUPPORT_EMAIL)
);

export default function TermsAndConditions() {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState("section-1");
  const [sectionHistory, setSectionHistory] = useState([]);
  const sectionRefs = useRef([]);
  const tocRef = useRef(null);

  // Auto-scroll TOC list to keep the active link visible
  useEffect(() => {
    if (!tocRef.current) return;
    const active = tocRef.current.querySelector(".pages-toc-link.active");
    if (active) active.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeId]);

  // Clear overflow-x on ancestors so position:sticky works against the window
  useEffect(() => {
    const root = document.getElementById("root");
    document.documentElement.style.overflowX = "visible";
    document.body.style.overflowX = "visible";
    if (root) root.style.overflowX = "visible";
    return () => {
      document.documentElement.style.overflowX = "";
      document.body.style.overflowX = "";
      if (root) root.style.overflowX = "";
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 8) {
        const last = sectionRefs.current[sectionRefs.current.length - 1];
        if (last) { setActiveId(last.id); return; }
      }
      const threshold = window.innerHeight * 0.25;
      let current = "section-1";
      sectionRefs.current.forEach((el) => {
        if (el && el.getBoundingClientRect().top <= threshold) current = el.id;
      });
      setActiveId(current);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = window.scrollY + el.getBoundingClientRect().top - 20;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo({ top: Math.min(top, max), behavior: "smooth" });
  };

  const scrollToSection = (e, id) => {
    e.preventDefault();
    setSectionHistory((prev) => [...prev, activeId]);
    scrollTo(id);
    setActiveId(id);
  };

  const handleBack = () => {
    if (sectionHistory.length > 0) {
      const prevId = sectionHistory[sectionHistory.length - 1];
      setSectionHistory((prev) => prev.slice(0, -1));
      scrollTo(prevId);
      setActiveId(prevId);
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="pages-page">

      {/* ── Top bar aligned with grid columns ── */}
      <div className="pages-top">
        <div />{/* spacer — aligns with TOC column */}
        <div className="pages-top-content">
          <div>
            <h1 className="pages-title">Terms &amp; Conditions</h1>
            <p className="pages-meta">
              VisuN AI Employee Monitoring &nbsp;·&nbsp;
              <span className="pages-meta-badge">Last updated: {LAST_UPDATED}</span>
              &nbsp;·&nbsp;
              <span className="pages-section-count">{SECTIONS.length} sections</span>
            </p>
          </div>
          <button className="pages-close-btn" onClick={() => navigate(-1)}>
            Close &amp; Return
          </button>
        </div>
      </div>

      <div className="pages-doc-layout">

        {/* ── Sticky TOC sidebar ── */}
        <aside className="pages-toc">
          <div className="pages-toc-header">
            <button className="pages-toc-back" onClick={handleBack}>
              <ArrowLeft size={12} /> Back
            </button>
            <span>Contents</span>
          </div>
          <div className="pages-toc-scroll">
            <nav aria-label="Table of contents" ref={tocRef}>
              <ol className="pages-toc-list">
                {SECTIONS.map((s, i) => {
                  const id = `section-${i + 1}`;
                  return (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        className={`pages-toc-link${activeId === id ? " active" : ""}`}
                        onClick={(e) => scrollToSection(e, id)}
                      >
                        {s.title}
                      </a>
                    </li>
                  );
                })}
              </ol>
            </nav>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="pages-content">

          {SECTIONS.map((s, i) => {
            const id = `section-${i + 1}`;
            return (
              <section
                key={id}
                id={id}
                ref={(el) => (sectionRefs.current[i] = el)}
                className="pages-section"
              >
                <h2 className="pages-section-title">{s.title}</h2>
                <div className="pages-section-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.body}</ReactMarkdown>
                </div>
              </section>
            );
          })}

          <div className="pages-footer">
            <span className="pages-footer-note">
              Global Cyber Associates Private Limited · Hyderabad, India
            </span>
          </div>
        </main>

      </div>
    </div>
  );
}
